import React, { useState } from 'react';
import { Select, InputAdornment, MenuItem } from '@material-ui/core';

import { useDefaultInputHook } from '../hooks';
import { DisplayItem } from '../../../types';
import InputProps from '../InputProps';

import { generateProperties, renderOption, DropDownIcon } from '../helpers';
import BaseInputWrapper from '../BaseInputWrapper';

interface DropDownProps extends InputProps<string, DisplayItem> {
  /** The list of display options */
  options: DisplayItem[];
}

const DropDown = ({
  inputId,
  inputName,
  inputLabel,
  options,
  onChange,
  inputError = false,
  inputErrorMessage = '',
  isDisabled = false,
  defaultValue = '',
  dataSource,
}: DropDownProps): JSX.Element => {
  const formInputProps = generateProperties<string, DisplayItem>({
    inputName,
    onChange,
    defaultValue,
    inputError,
    inputErrorMessage,
    dataSource,
  });

  const {
    currentClass,
    decoration,
    onBlur,
    hasDecoration,
  } = useDefaultInputHook({
    error: formInputProps.error,
    defaultValue: formInputProps.value,
    loading: false,
    config: {
      greedyCheck: true,
    },
  });

  const [isDirty, toggleIsDirty] = useState(false);

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>): void => {
    const valueOfDropDown = event.target.value as string;
    const result = options
      .filter((item: DisplayItem): boolean => item.Value === valueOfDropDown)
      .pop();
    if (typeof result !== 'undefined') {
      formInputProps.onChange(result);
      toggleIsDirty(true);
    }
  };

  function valueIsSelected(): boolean {
    return (
      typeof options
        .filter((option): boolean => option.Value === formInputProps.value)
        .pop() !== 'undefined' && isDirty
    );
  }

  // Documentation is not clear on what type of value this method should return
  // to work with IconComponent (React.Element does not work), so for now the
  // return type will be ignored
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function iconComponent() {
    return !valueIsSelected() ? (
      <DropDownIcon data-testid="drop-down-icon" />
    ) : (
      <></>
    );
  }

  function renderValue(value: unknown): React.ReactNode {
    let renderedOption = <div />;
    options.forEach((option: DisplayItem): void => {
      if (option.Value === value) {
        renderedOption = <div>{option.Text}</div>;
      }
    });
    return renderedOption;
  }

  return (
    <BaseInputWrapper
      inputId={inputId}
      label={inputLabel}
      error={formInputProps.error}
      errorMessage={formInputProps.errorText}
      disabled={isDisabled}
    >
      <Select
        id={inputId}
        name={formInputProps.name}
        className={currentClass}
        value={formInputProps.value}
        onChange={(event): void => {
          handleChange(event);
          onBlur();
        }}
        error={formInputProps.error}
        disabled={isDisabled}
        onBlur={onBlur}
        IconComponent={iconComponent}
        renderValue={renderValue}
        endAdornment={
          hasDecoration && isDirty ? (
            <InputAdornment position="end">{decoration}</InputAdornment>
          ) : undefined
        }
      >
        {options.map(
          (option): React.ReactNode => (
            <MenuItem key={option.Value} value={option.Value}>
              {renderOption(option, option.Value === defaultValue)}
            </MenuItem>
          ),
        )}
      </Select>
    </BaseInputWrapper>
  );
};

export default DropDown;
